import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  Bell,
  FileText,
  FolderKanban,
  MessageSquareText,
  Search,
  Users,
} from "lucide-react";

const navItems = [
  ["หน้าหลัก", "/"],
  ["KHORN Web App", "/product"],
  ["KHORN LINE", "/line"],
  ["KHORN MCP", "/mcp"],
  ["ราคา", "/#pricing"],
  ["ความปลอดภัย", "/privacy"],
  ["ติดต่อ", "/contact"],
];

const capabilities = [
  ["รับข้อมูลเข้า", "รวม LINE อีเมล ไฟล์ และข้อความทีมไว้ในพื้นที่ทำงานเดียว", MessageSquareText],
  ["จัดเคส", "แยกคู่ความ ประเด็น หลักฐาน สิ่งที่ยังขาด และงานที่ต้องทำต่อ", FolderKanban],
  ["มอบหมายงาน", "กำหนดผู้รับผิดชอบ กำหนดเวลา และสถานะงานให้ทีมเห็นตรงกัน", Users],
  ["ร่างเอกสาร", "เตรียมหนังสือทวงถาม สัญญา รายการตรวจ และร่างงานจากข้อมูลคดี", FileText],
  ["ค้นข้อมูลประกอบ", "ใช้ฐานกฎหมาย เอกสาร และข้อมูลคดีเพื่อช่วยทีมตรวจต่อ", Search],
  ["ติดตามงาน", "แจ้งเตือนงานค้าง กำหนดเวลา และสิ่งที่ทำให้เคสไม่เดินต่อ", Bell],
];

const Header = () => (
  <header className="sticky top-0 z-50 border-b border-[#E8EAE5] bg-white/90 backdrop-blur-xl">
    <div className="mx-auto flex max-w-[1440px] items-center justify-between px-4 py-4 md:px-6">
      <Link to="/" className="flex items-center gap-3" aria-label="KHORN AI home">
        <span className="relative block h-11 w-[172px] shrink-0 overflow-hidden">
          <img src="/logo_and_name.png" alt="KHORN AI" className="absolute -left-[28px] -top-[60px] h-[168px] max-w-none" />
        </span>
      </Link>

      <nav className="hidden items-center gap-8 text-[13px] text-[#5F665E] md:flex">
        {navItems.map(([label, href]) => (
          <a key={label} href={href} className="hover:text-[#15130F]">
            {label}
          </a>
        ))}
      </nav>

      <a href="https://app.khorn-ai.com/plan" className="rounded-full bg-[#15130F] px-5 py-2.5 text-[13px] font-medium text-[#FAFAF7] transition hover:bg-[#1F2A22]">
        เริ่มใช้งาน
      </a>
    </div>
  </header>
);

export default function ProductPage() {
  return (
    <main className="min-h-screen bg-white text-[#15130F]">
      <Helmet>
        <title>KHORN Web App | ระบบจัดการงานคดีสำหรับสำนักงานกฎหมาย</title>
        <meta
          name="description"
          content="KHORN Web App สำหรับจัดการงานคดี รับข้อมูลจาก LINE อีเมล และไฟล์ จัดเคส มอบหมายงาน ร่างเอกสาร ค้นข้อมูลประกอบ และติดตามงานค้าง"
        />
        <meta name="keywords" content="KHORN Web App, ระบบจัดการคดี, ระบบสำนักงานกฎหมาย, AI สำนักงานกฎหมาย, legal workflow" />
        <meta property="og:title" content="KHORN Web App | ระบบจัดการงานคดีสำหรับสำนักงานกฎหมาย" />
        <meta property="og:description" content="รับข้อมูล จัดเคส ร่างเอกสาร มอบหมายงาน และติดตามงานค้างในระบบเดียว" />
        <meta property="og:url" content="https://www.khorn-ai.com/product" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.khorn-ai.com/product" />
      </Helmet>

      <Header />

      <section className="px-5 py-20 md:px-10 md:py-28">
        <div className="mx-auto grid max-w-[1280px] items-center gap-16 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <div className="mb-6 inline-flex items-center gap-3 rounded-full bg-[#F6F8F4] px-4 py-2">
              <img src="/images/google-chrome-logo.svg" alt="" aria-hidden="true" className="h-5 w-5" />
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5F665E]">
                KHORN Web App
              </span>
            </div>
            <h1 className="max-w-4xl text-6xl font-semibold leading-[0.95] tracking-[-0.07em] md:text-8xl">
              ส่งข้อมูลเข้า แล้วให้ระบบจัดงานคดีให้พร้อมตรวจ
            </h1>
            <p className="mt-7 max-w-2xl text-xl leading-9 text-[#5F665E]">
              สำหรับสำนักงานที่ต้องการเห็นภาพรวมลูกความ เคส ทีม เอกสาร งานค้าง และสิ่งที่ต้องทำต่อในที่เดียว
            </p>
            <a
              href="https://app.khorn-ai.com/plan"
              className="mt-9 inline-flex h-14 items-center justify-center gap-3 rounded-xl bg-[#15130F] px-8 text-base font-semibold text-white shadow-[0_8px_18px_rgba(0,0,0,0.16)] transition hover:bg-[#1F2A22]"
            >
              ไปที่แพ็กเกจ
              <ArrowRight size={18} />
            </a>
          </div>

          <WebAppMock />
        </div>
      </section>

      <section className="border-y border-[#E8EAE5] bg-[#FAFAF7] px-5 py-20 md:px-10 md:py-24">
        <div className="mx-auto max-w-[1280px]">
          <div className="mb-12 max-w-4xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#6A7168]">
              ทำอะไรได้บ้าง
            </p>
            <h2 className="mt-5 text-5xl font-semibold leading-[0.98] tracking-[-0.06em] md:text-7xl">
              งานสำนักงานที่ไม่ควรกระจายอยู่หลายที่
            </h2>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {capabilities.map(([title, desc, Icon]) => (
              <article key={title} className="rounded-[28px] bg-white p-7 shadow-[0_18px_50px_rgba(22,29,24,0.06)]">
                <Icon size={28} className="text-[#6A7168]" />
                <h3 className="mt-8 text-2xl font-semibold tracking-[-0.04em]">{title}</h3>
                <p className="mt-4 text-base leading-8 text-[#5F665E]">{desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 py-20 md:px-10 md:py-24">
        <div className="mx-auto grid max-w-[1180px] gap-10 lg:grid-cols-3">
          {[
            ["01", "ตั้งเคส", "นำข้อมูลลูกความ เอกสาร และงานที่มีอยู่เข้าระบบ"],
            ["02", "เตรียมงาน", "KHORN ช่วยสรุป จัดเคส ร่างเอกสาร และหาข้อมูลประกอบ"],
            ["03", "ติดตามต่อ", "ทีมเห็นงานต่อ กำหนดเวลา และสิ่งที่ยังค้างในแดชบอร์ด"],
          ].map(([num, title, text]) => (
            <article key={num} className="rounded-[28px] border border-[#E8EAE5] bg-white p-8">
              <p className="text-sm font-semibold text-[#7C8579]">{num}</p>
              <h3 className="mt-8 text-3xl font-semibold tracking-[-0.05em]">{title}</h3>
              <p className="mt-4 text-base leading-8 text-[#5F665E]">{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="px-5 pb-20 md:px-10">
        <div className="mx-auto flex max-w-[1180px] flex-col items-center justify-between gap-6 rounded-[32px] bg-[#15130F] p-8 text-white md:flex-row md:p-10">
          <div>
            <h2 className="text-3xl font-semibold tracking-[-0.04em]">พร้อมเริ่มจัดงานคดีในระบบเดียว</h2>
            <p className="mt-3 text-white/60">เลือกแพ็กเกจเดี่ยวหรือทีม แล้วเริ่มใช้งาน KHORN Web App</p>
          </div>
          <a href="https://app.khorn-ai.com/plan" className="inline-flex h-14 shrink-0 items-center justify-center rounded-xl bg-white px-8 text-base font-semibold text-[#15130F]">
            ไปที่แพ็กเกจ
          </a>
        </div>
      </section>
    </main>
  );
}

function WebAppMock() {
  return (
    <article className="relative mx-auto w-full max-w-3xl">
      <div className="rounded-[22px] border border-[#D9DDD6] bg-[#E8EAE5] p-px shadow-[0_24px_70px_rgba(22,29,24,0.08)]">
        <div className="overflow-hidden rounded-[19px] bg-white">
          <div className="flex h-9 items-center gap-2 border-b border-[#ECEFEB] bg-[#FAFAF7] px-4">
            <span className="h-3 w-3 rounded-full bg-[#FF5F57]" />
            <span className="h-3 w-3 rounded-full bg-[#FFBD2E]" />
            <span className="h-3 w-3 rounded-full bg-[#28C840]" />
            <div className="mx-auto hidden h-6 w-64 items-center justify-center rounded-full bg-white text-[10px] font-semibold text-[#8A9288] md:flex">
              app.khorn-ai.com
            </div>
          </div>

          <div className="grid min-h-[390px] grid-cols-[156px_1fr] text-[#15130F] sm:grid-cols-[190px_1fr]">
            <aside className="border-r border-[#ECEFEB] bg-[#FAFAF7] p-4">
              <div className="mb-7 flex items-center gap-2">
                <img src="/logo.png" alt="" aria-hidden="true" className="h-5 w-5 object-contain" />
                <span className="text-xs font-semibold">KHORN AI</span>
              </div>
              <div className="space-y-3 text-[11px] font-semibold text-[#7C8579]">
                {["เริ่มงานใหม่", "สำนักงาน", "แฟ้มคดีทั้งหมด"].map((item) => (
                  <div key={item} className="rounded-xl bg-white px-3 py-2">
                    {item}
                  </div>
                ))}
              </div>
              <p className="mt-7 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#A3AAA0]">
                แฟ้มคดี
              </p>
              <div className="mt-3 space-y-2 text-[10px] font-semibold text-[#5F665E]">
                {["คดีรุกล้ำที่ดินคุณสมชาย", "คดีซื้อโฆษณา", "ข้อพิพาทสัญญา ABC", "คดีแรงงานเลิกจ้าง"].map((item) => (
                  <div key={item} className="truncate rounded-lg px-2 py-2">▱ {item}</div>
                ))}
              </div>
            </aside>

            <main className="relative flex flex-col items-center justify-center p-6">
              <div className="mb-7 flex items-center gap-3">
                <img src="/logo.png" alt="" aria-hidden="true" className="h-7 w-7 object-contain" />
                <h3 className="text-xl font-semibold tracking-[-0.04em] md:text-2xl">
                  ให้ค้อนเอไอช่วยงานคดียังไงดีครับ
                </h3>
              </div>

              <div className="w-full max-w-md rounded-[24px] bg-white p-3 shadow-[0_18px_60px_rgba(22,29,24,0.10)]">
                <div className="min-h-[74px] rounded-[18px] border border-[#ECEFEB] px-4 py-3 text-sm text-[#A3AAA0]">
                  ถามคำถาม...
                </div>
                <div className="mt-3 flex items-center justify-between px-2 text-[#8A9288]">
                  <span className="text-xl">＋</span>
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-[#D8DBD6] text-white">↑</span>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap justify-center gap-2">
                {["มาตรา", "ฎีกา", "ร่างเอกสาร", "ขั้นตอนงาน"].map((item) => (
                  <span key={item} className="rounded-full border border-[#DDE3D8] px-4 py-2 text-xs font-semibold text-[#6A7168]">
                    {item}
                  </span>
                ))}
              </div>

              <AnimatedMascot className="absolute bottom-5 right-6 h-20 w-24 opacity-90" />
            </main>
          </div>
        </div>
      </div>
    </article>
  );
}

function AnimatedMascot({ className = "", mood = "working" }) {
  return (
    <span className={`${className} block`}>
      <img src={`/animation/${mood}_1.png`} alt="" aria-hidden="true" className="mascot-frame-a absolute inset-0 h-full w-full object-contain" />
      <img src={`/animation/${mood}_2.png`} alt="" aria-hidden="true" className="mascot-frame-b absolute inset-0 h-full w-full object-contain" />
    </span>
  );
}
